import type { RunState } from '../../components/TruthLabel.tsx';
import type { ContainerStage, ContainerView } from '../../features/onboarding/api.ts';
import type {
  Attempt,
  TraceiraWorkspaceDiscoveryRecordsV1,
} from '../../contracts/workspace/v1/index.ts';

export interface OverviewTimelineEvent {
  id: string;
  at: string;
  actor: string;
  text: string;
  state: RunState;
}

const stateForStage: Record<ContainerStage['status'], RunState> = {
  done: 'COMPLETED',
  active: 'RUNNING',
  pending: 'PROPOSED',
  failed: 'FAILED',
};

const descriptionForStage: Record<ContainerStage['status'], string> = {
  done: 'completed',
  active: 'in progress',
  pending: 'pending',
  failed: 'failed',
};

const hasTimestamp = (stage: ContainerStage): stage is ContainerStage & { at: string } =>
  typeof stage.at === 'string' && Number.isFinite(Date.parse(stage.at));

type Records = Pick<
  TraceiraWorkspaceDiscoveryRecordsV1,
  'attempt' | 'findings' | 'findingEngagementLinks'
>;

const stateForAttempt: Partial<Record<Attempt['status'], RunState>> = {
  COMPLETE: 'COMPLETED',
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
  STOPPED: 'CANCELLED',
};

/**
 * Recent changes from recorded state only: container lifecycle (one line once setup is done),
 * the latest discovery attempt's Start and outcome, and engagements opened from findings.
 */
export function overviewTimelineEvents(
  container: ContainerView | null,
  records?: Records | null,
): OverviewTimelineEvent[] {
  const stages = (container?.stages ?? []).filter(hasTimestamp);
  const events: OverviewTimelineEvent[] =
    stages.length > 0 && stages.every((stage) => stage.status === 'done')
      ? [
          {
            id: 'container-ready',
            at: stages.reduce((latest, stage) => (stage.at > latest ? stage.at : latest), ''),
            actor: 'Workspace container',
            text: 'Workspace container ready',
            state: 'COMPLETED',
          },
        ]
      : stages.map((stage) => ({
          id: `container-stage:${stage.name}`,
          at: stage.at,
          actor: 'Workspace container',
          text: `${stage.label} ${descriptionForStage[stage.status]}`,
          state: stateForStage[stage.status],
        }));

  const attempt = records?.attempt;
  if (attempt) {
    events.push({
      id: `attempt-start:${attempt.attemptId}`,
      at: attempt.start.requestedAt,
      actor: attempt.start.requestedBy?.displayName ?? 'Workspace owner',
      text: 'Started discovery',
      state: 'READY',
    });
    const finished = stateForAttempt[attempt.status];
    if (finished && attempt.finishedAt) {
      const count = records?.findings.length ?? 0;
      events.push({
        id: `attempt-end:${attempt.attemptId}`,
        at: attempt.finishedAt,
        actor: 'Discovery',
        text: `Discovery ${attempt.status === 'COMPLETE' ? 'completed' : attempt.status.toLowerCase()} · ${count} ${count === 1 ? 'finding' : 'findings'}`,
        state: finished,
      });
    }
  }
  for (const link of records?.findingEngagementLinks ?? []) {
    const title = records?.findings.find((f) => f.findingId === link.findingId)?.title;
    events.push({
      id: `engagement:${link.linkId}`,
      at: link.requestedAt,
      actor: link.requestedBy.displayName,
      text: title ? `Opened an engagement for “${title}”` : 'Opened an engagement from a finding',
      state: 'COMPLETED',
    });
  }

  return events.sort((left, right) => Date.parse(right.at) - Date.parse(left.at));
}
